import { createFileRoute, Link } from "@tanstack/react-router";
import { PortableText } from "@portabletext/react";
import { sanityClient } from "@/lib/sanity";
import { useLocale, pickLocalized, pickLocalizedRaw, tr, t } from "@/lib/i18n";
import { portableComponents } from "@/lib/portable-text";
import { pageMeta } from "@/lib/seo";

const DESCRIPTION =
  "The terms that govern your use of the Matchamind app by Anatol Labs, including account deletion, subscriptions, and acceptable use.";

const QUERY = `*[_type == "termsAndConditions"][0]{ title, content, version, _createdAt, _updatedAt }`;

export const Route = createFileRoute("/terms")({
  loader: () => sanityClient.fetch<any>(QUERY),
  head: () =>
    pageMeta({
      title: "Terms of Use — Matchamind",
      description: DESCRIPTION,
      path: "/terms",
    }),
  component: TermsPage,
});

function formatDate(iso: string | undefined, locale: "en" | "fr"): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(locale === "fr" ? "fr-FR" : "en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "UTC",
    });
  } catch {
    return "";
  }
}

function TermsPage() {
  const locale = useLocale();
  const search = locale === "en" ? {} : { lang: "fr" as const };
  const data = Route.useLoaderData();

  if (!data) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24">
        <p className="text-text-muted">{tr(t.common.notFound, locale)}</p>
        <Link to="/" search={search} className="mt-4 inline-block underline">
          ← {locale === "fr" ? "Retour à l'accueil" : "Back home"}
        </Link>
      </div>
    );
  }

  const body = pickLocalizedRaw<any[]>(data.content, locale);

  return (
    <article className="flex w-full flex-col items-center gap-8 pb-16">
      <header className="mx-auto flex w-full max-w-[1200px] flex-col items-start gap-6 px-6 py-8 md:px-10 md:py-12 lg:px-20">
        <Link
          to="/"
          search={search}
          className="text-sm text-text-muted hover:text-text-primary"
        >
          ← {locale === "fr" ? "Retour à l'accueil" : "Back home"}
        </Link>
        <h1 className="font-display text-[28px] font-semibold leading-[36px] tracking-[0.2px] text-text-primary md:text-[34px] md:leading-[44px]">
          {pickLocalized(data.title, locale)}
        </h1>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1 font-sans text-[14px] font-light text-text-muted">
          {data.version && (
            <span>
              {locale === "fr" ? "Version" : "Version"} {data.version}
            </span>
          )}
          {data._createdAt && (
            <span>
              {locale === "fr" ? "Créée le" : "Created"}{" "}
              {formatDate(data._createdAt, locale)}
            </span>
          )}
          {data._updatedAt && (
            <span>
              {locale === "fr" ? "Dernière mise à jour" : "Last updated"}{" "}
              {formatDate(data._updatedAt, locale)}
            </span>
          )}
        </div>
      </header>

      <div className="mx-auto w-full max-w-[840px] px-6 md:px-10">
        {body && <PortableText value={body} components={portableComponents as any} />}
      </div>
    </article>
  );
}
